import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { Close, WhatsApp } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      overflow: "hidden",
      maxWidth: 420,
      width: "100%",
      [theme.breakpoints.down("sm")]: {
        borderRadius: 0,
        maxWidth: "100%",
        margin: 0,
        width: "100%",
        height: "100%",
        maxHeight: "100%",
      },
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 2.5, 2),
    borderBottom:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(37, 211, 102, 0.14)",
    color: "#25D366",
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 700,
    fontSize: "1.125rem",
    lineHeight: 1.3,
  },
  subtitle: {
    marginTop: 2,
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3, 2.5, 3.5),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2, 4),
      flex: 1,
      justifyContent: "center",
    },
  },
  qrWrapper: {
    padding: theme.spacing(2),
    borderRadius: 16,
    backgroundColor: "#fff",
    boxShadow:
      theme.mode === "light"
        ? "0 8px 32px rgba(15, 23, 42, 0.12)"
        : "0 8px 32px rgba(0, 0, 0, 0.35)",
    marginBottom: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 280,
    minWidth: 280,
    [theme.breakpoints.down("sm")]: {
      minHeight: 240,
      minWidth: 240,
      padding: theme.spacing(1.5),
    },
  },
  hint: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    maxWidth: 320,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    backgroundColor:
      theme.mode === "light"
        ? "rgba(37, 211, 102, 0.08)"
        : "rgba(37, 211, 102, 0.12)",
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [qrCode, setQrCode] = useState("");
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, socket, user.companyId]);

  const qrSize = isMobile ? 220 : 256;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      className={classes.dialog}
      scroll="paper"
    >
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <WhatsApp />
        </Box>
        <Box className={classes.headerText}>
          <Typography className={classes.title}>
            {i18n.t("qrCode.title")}
          </Typography>
          <Typography className={classes.subtitle}>
            {i18n.t("qrCode.subtitle")}
          </Typography>
        </Box>
        <IconButton
          aria-label={i18n.t("common.close")}
          onClick={onClose}
          className={classes.closeButton}
          edge="end"
        >
          <Close />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.qrWrapper}>
          {qrCode ? (
            <QRCode value={qrCode} size={qrSize} />
          ) : (
            <Box className={classes.loading}>
              <CircularProgress size={36} />
              <Typography variant="body2">
                {i18n.t("qrCode.waiting")}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography className={classes.hint}>
          {i18n.t("qrCode.hint")}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
